import { useEffect, useState } from "react";
import { useStation } from "@/context/StationContext";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { fmtMYR, fmtL, fmtDate, PRODUCT_COLORS } from "@/lib/format";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  PieChart, Pie, Cell, Legend,
} from "recharts";
import {
  Wallet, Fuel, Store, AlertTriangle, TrendingUp, Loader2, ArrowUpRight, BellRing,
} from "lucide-react";

const glass =
  "rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur-xl shadow-[0_8px_32px_rgba(0,0,0,0.4)]";

function StatCard({ icon: Icon, label, value, sub, tint, testid }) {
  return (
    <div className={`${glass} p-5 sm:p-6 relative overflow-hidden animate-fadeUp`} data-testid={testid}>
      <div className={`absolute -right-6 -top-6 w-24 h-24 rounded-full blur-2xl opacity-30`} style={{ background: tint }} />
      <div className="flex items-center justify-between relative">
        <span className="text-xs font-mono uppercase tracking-widest text-[#9CA3AF]">{label}</span>
        <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: `${tint}22`, color: tint }}>
          <Icon className="w-4 h-4" />
        </div>
      </div>
      <div className="metric-value text-2xl sm:text-3xl font-bold mt-3 text-white tracking-tight">{value}</div>
      {sub && <div className="text-xs text-[#9CA3AF] mt-1">{sub}</div>}
    </div>
  );
}

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-lg border border-[#374151] bg-[#1F2937] px-3 py-2 text-xs">
      <div className="text-[#9CA3AF] font-mono mb-1">{label}</div>
      {payload.map((p) => (
        <div key={p.name} className="flex items-center gap-2" style={{ color: p.color }}>
          <span>{p.name}:</span>
          <span className="font-mono font-semibold">{p.name === "liters" ? fmtL(p.value) : fmtMYR(p.value)}</span>
        </div>
      ))}
    </div>
  );
};

export default function Dashboard() {
  const { user } = useAuth();
  const { stationId: station, setStationId: setStation, stations } = useStation();
  const [month, setMonth] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pending, setPending] = useState(null);
  const [applying, setApplying] = useState(false);
  const [checking, setChecking] = useState(false);
  const [reload, setReload] = useState(0);
  const isManager = user?.role === "manager";

  useEffect(() => {
    if (!isManager) return;
    api.get("/official-prices/pending")
      .then((r) => setPending(r.data && r.data.status === "pending" ? r.data : null))
      .catch(() => {});
  }, [isManager, reload]);

  useEffect(() => {
    setLoading(true);
    const params = [];
    if (station !== "all") params.push(`station_id=${station}`);
    if (month) params.push(`month=${month}`);
    const q = params.length ? `?${params.join("&")}` : "";
    api.get(`/dashboard${q}`)
      .then((r) => { setData(r.data); if (!month && r.data?.month) setMonth(r.data.month); })
      .catch(() => {}).finally(() => setLoading(false));
  }, [station, month, reload]);

  const fmtDDMM = (d) => fmtDate(d);
  const applyPrices = async () => {
    setApplying(true);
    try {
      const { data: res } = await api.post("/official-prices/apply");
      toast.success(`Applied to ${res.updated} pumps & pushed to all stations`);
      setPending(null);
      setReload((x) => x + 1);
    } catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
    setApplying(false);
  };
  const dismissPrices = async () => {
    try { await api.post("/official-prices/dismiss"); setPending(null); }
    catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
  };
  const checkPrices = async () => {
    setChecking(true);
    try {
      const { data: doc } = await api.post("/official-prices/stage", {});
      if (doc && doc.status === "pending") {
        setPending(doc);
        toast.success("Latest official prices loaded — review and apply below");
      } else {
        toast.info("No new official prices found right now. Try again after Wednesday's update.");
      }
    } catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
    setChecking(false);
  };

  const s = data?.summary;
  const pie = (data?.by_product || []).map((p) => ({ name: p.product, value: Math.round(p.liters) }));

  return (
    <div className="space-y-6">
      {pending && (
        <div className="rounded-2xl border border-amber-500/40 bg-amber-500/10 p-4 sm:p-5 flex flex-col lg:flex-row lg:items-center justify-between gap-3 animate-fadeUp" data-testid="official-prices-banner">
          <div>
            <div className="flex items-center gap-2 text-amber-400 font-semibold">
              <BellRing className="w-4 h-4" /> Official prices available{pending.effective_date ? ` (effective ${fmtDDMM(pending.effective_date)})` : ""}
            </div>
            <div className="text-sm text-white/90 mt-1.5 flex flex-wrap gap-x-5 gap-y-1">
              {Object.entries(pending.prices || {}).map(([k, v]) => (
                <span key={k}>{k}: <b className="metric-value">{fmtMYR(v)}</b>/L</span>
              ))}
            </div>
            <div className="text-xs text-[#9CA3AF] mt-1">Source: {pending.source} · applying keeps each pump's fixed margin and pushes to all stations</div>
          </div>
          <div className="flex gap-2 shrink-0">
            <Button onClick={applyPrices} disabled={applying} data-testid="apply-official-prices" className="bg-amber-500 hover:bg-amber-400 text-black font-semibold">
              {applying ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null} Apply &amp; push to all
            </Button>
            <Button variant="outline" onClick={dismissPrices} data-testid="dismiss-official-prices" className="border-[#374151] bg-transparent hover:bg-[#1F2937] text-white">Dismiss</Button>
          </div>
        </div>
      )}
      {(data?.variance_alerts?.length || 0) > 0 && (
        <div className="rounded-2xl border border-rose-500/40 bg-rose-500/10 p-4 sm:p-5 animate-fadeUp" data-testid="variance-alert-card">
          <div className="flex items-center gap-2 text-rose-400 font-semibold">
            <AlertTriangle className="w-4 h-4" /> Stock variance alerts · gap over {fmtL(data.variance_threshold_l)}
          </div>
          <div className="text-xs text-[#9CA3AF] mt-1">Products where Delivered−Sold or Meter−Sales differs by more than the threshold this month.</div>
          <div className="mt-3 divide-y divide-white/10">
            {data.variance_alerts.map((v, i) => (
              <div key={i} className="flex flex-wrap items-center justify-between gap-x-6 gap-y-1 py-2.5" data-testid={`variance-alert-${i}`}>
                <div className="text-sm text-white font-medium min-w-0">
                  {v.station} · <span className="text-rose-300">{v.product}</span>
                </div>
                <div className="flex flex-wrap gap-x-5 gap-y-1 text-xs text-white/80 items-center">
                  <span>Delivered <b className="metric-value text-white">{fmtL(v.delivered)}</b></span>
                  <span>Meter sold <b className="metric-value text-white">{fmtL(v.meter_sold)}</b></span>
                  <span>Sales sold <b className="metric-value text-white">{fmtL(v.sales_sold)}</b></span>
                  {v.reasons.includes("delivered_vs_sold") && <span className="px-2 py-0.5 rounded bg-rose-500/20 text-rose-300">Deliv−Sold {fmtL(v.gap_delivered)}</span>}
                  {v.reasons.includes("meter_vs_sales") && <span className="px-2 py-0.5 rounded bg-amber-500/20 text-amber-300">Meter−Sales {fmtL(v.gap_sales)}</span>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <div className="text-xs font-mono uppercase tracking-widest text-emerald-400 mb-1">Group Overview · {data?.month}</div>
          <h1 className="font-[Outfit] font-bold text-3xl sm:text-4xl tracking-tight">
            Welcome back, {user?.name}
          </h1>
        </div>
        <div className="flex gap-3 w-full sm:w-auto">
          {isManager && !pending && (
            <Button onClick={checkPrices} disabled={checking} variant="outline" data-testid="check-official-prices"
              className="border-amber-500/40 text-amber-400 bg-transparent hover:bg-amber-500/10 shrink-0">
              {checking ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <BellRing className="w-4 h-4 mr-2" />}
              Check official prices
            </Button>
          )}
          <div className="w-40">
            <Input type="month" data-testid="dashboard-month-input" value={month}
              onChange={(e) => setMonth(e.target.value)}
              className="bg-[#111827] border-[#374151] text-white" />
          </div>
          <div className="flex-1 sm:w-56">
            <Select value={station} onValueChange={setStation}>
              <SelectTrigger data-testid="station-filter-dropdown" className="bg-[#111827] border-[#374151] text-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#111827] border-[#374151] text-white">
                <SelectItem value="all">All Stations</SelectItem>
                {stations.map((st) => (
                  <SelectItem key={st.id} value={st.id}>{st.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {loading || !s ? (
        <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 text-emerald-400 animate-spin" /></div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 sm:gap-6">
            {(data.station_revenue || []).map((sr) => (
              <StatCard key={sr.station_id} testid={`card-site-revenue-${sr.station_id}`} icon={Wallet} label={sr.station || "Site"} tint="#10B981"
                value={fmtMYR(sr.revenue)} sub={`Monthly revenue · ${data.month}`} />
            ))}
            <StatCard testid="card-mart-sales" icon={Store} label="Mart Sales" tint="#8B5CF6"
              value={fmtMYR(s.mart_sales)} sub="Convenience store this month" />
            <StatCard testid="card-pump-alerts" icon={AlertTriangle} label="Active Pump Alerts" tint="#F59E0B"
              value={s.pump_alerts} sub={s.pump_alerts ? "Pumps need price setup" : "All pumps configured"} />
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 sm:gap-6">
            <div className={`${glass} p-5 sm:p-6 xl:col-span-2`} data-testid="daily-sales-chart">
              <div className="flex items-center gap-2 mb-4">
                <TrendingUp className="w-4 h-4 text-emerald-400" />
                <h3 className="font-[Outfit] font-semibold text-lg">Daily Sales Trends</h3>
              </div>
              <ResponsiveContainer width="100%" height={280}>
                <LineChart data={data.daily} margin={{ left: -10, right: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1F2937" vertical={false} />
                  <XAxis dataKey="date" tick={{ fill: "#9CA3AF", fontSize: 11 }} tickFormatter={(d) => d?.slice(8)} />
                  <YAxis tick={{ fill: "#9CA3AF", fontSize: 11 }} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
                  <Tooltip content={<CustomTooltip />} />
                  <Line type="monotone" dataKey="revenue" name="revenue" stroke="#10B981" strokeWidth={2.5} dot={false} />
                  <Line type="monotone" dataKey="profit" name="profit" stroke="#3B82F6" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>

            <div className={`${glass} p-5 sm:p-6`} data-testid="fuel-grade-chart">
              <h3 className="font-[Outfit] font-semibold text-lg mb-4">Fuel Grade Distribution</h3>
              {pie.length ? (
                <ResponsiveContainer width="100%" height={280}>
                  <PieChart>
                    <Pie data={pie} dataKey="value" nameKey="name" cx="50%" cy="45%" innerRadius={55} outerRadius={90} paddingAngle={3}>
                      {pie.map((e) => <Cell key={e.name} fill={PRODUCT_COLORS[e.name] || "#10B981"} stroke="none" />)}
                    </Pie>
                    <Tooltip content={<CustomTooltip />} />
                    <Legend wrapperStyle={{ fontSize: 12, color: "#9CA3AF" }} />
                  </PieChart>
                </ResponsiveContainer>
              ) : <div className="text-[#9CA3AF] text-sm py-20 text-center">No sales yet</div>}
            </div>
          </div>

          <div className={`${glass} p-5 sm:p-6`} data-testid="recent-activity">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-[Outfit] font-semibold text-lg">Recent Activity</h3>
              <span className="text-xs font-mono text-emerald-400 flex items-center gap-1">
                <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" /> LIVE
              </span>
            </div>
            <div className="divide-y divide-white/5">
              {(data.recent_activity || []).map((a, i) => (
                <div key={i} className="flex items-center justify-between py-3" data-testid={`activity-row-${i}`}>
                  <div className="flex items-center gap-3 min-w-0">
                    <span className={`text-[10px] font-mono px-2 py-1 rounded ${
                      a.type === "Meter" ? "bg-emerald-500/15 text-emerald-400" :
                      a.type === "Delivery" ? "bg-blue-500/15 text-blue-400" : "bg-violet-500/15 text-violet-400"
                    }`}>{a.type}</span>
                    <div className="min-w-0">
                      <div className="text-sm text-white truncate">{a.label}</div>
                      <div className="text-xs text-[#9CA3AF] truncate">{a.station} · {fmtDate(a.date)} · {a.by}</div>
                    </div>
                  </div>
                  <div className="metric-value text-sm font-semibold text-white flex items-center gap-1 whitespace-nowrap">
                    {fmtMYR(a.amount)} <ArrowUpRight className="w-3 h-3 text-emerald-400" />
                  </div>
                </div>
              ))}
              {!(data.recent_activity || []).length && (
                <div className="text-[#9CA3AF] text-sm py-8 text-center">No activity yet. Start entering daily operations.</div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
